import React, { useEffect, useRef } from 'react';
import { useColorScheme } from '@mui/joy/styles';

declare global {
  interface Window {
    require?: any;
    monaco?: any;
  }
}

const MONACO_VERSION = '0.45.0';
const MONACO_BASE = `https://cdn.jsdelivr.net/npm/monaco-editor@${MONACO_VERSION}/min/vs`;

let loaderPromise: Promise<any> | null = null;

const loadMonaco = (): Promise<any> => {
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    if (window.monaco) {
      resolve(window.monaco);
      return;
    }
    const script = document.createElement('script');
    script.src = `${MONACO_BASE}/loader.js`;
    script.onload = () => {
      const w = window as any;
      w.require.config({ paths: { vs: MONACO_BASE } });
      w.require(['vs/editor/editor.main'], () => {
        resolve(w.monaco);
      });
    };
    script.onerror = () => reject(new Error('Failed to load Monaco loader'));
    document.head.appendChild(script);
  });
  return loaderPromise;
};

interface Props {
  value: string;
  onChange: (value: string) => void;
  language?: string;
  height?: number | string;
  theme?: 'vs-dark' | 'vs' | 'auto';
}

export default function MonacoEditor({ value, onChange, language = 'html', height = 400, theme = 'auto' }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<any>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const { mode } = useColorScheme();

  const resolvedTheme = theme === 'auto' ? (mode === 'dark' ? 'vs-dark' : 'vs') : theme;

  useEffect(() => {
    let disposed = false;
    let sub: any;

    loadMonaco().then((monaco) => {
      if (disposed || !containerRef.current) return;
      const editor = monaco.editor.create(containerRef.current, {
        value: valueRef.current,
        language,
        theme: resolvedTheme,
        automaticLayout: true,
        minimap: { enabled: false },
        wordWrap: 'on',
        scrollBeyondLastLine: false,
        fontSize: 13,
        padding: { top: 12, bottom: 12 }
      });
      editorRef.current = editor;
      sub = editor.onDidChangeModelContent(() => {
        const v = editor.getValue();
        if (v !== valueRef.current) onChangeRef.current(v);
      });
    }).catch(err => console.error(err));

    return () => {
      disposed = true;
      if (sub) sub.dispose();
      if (editorRef.current) editorRef.current.dispose();
      editorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    if (window.monaco) {
      window.monaco.editor.setTheme(resolvedTheme);
    }
  }, [resolvedTheme]);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor && editor.getValue() !== value) {
      editor.setValue(value);
    }
  }, [value]);

  return (
    <div
      ref={containerRef}
      style={{
        height,
        width: '100%',
        border: '1px solid var(--joy-palette-divider)',
        borderRadius: 8,
        overflow: 'hidden'
      }}
    />
  );
}
